import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, Check, Phone, ChevronDown, ChevronRight, Store, Home as HomeIcon, Recycle, HandCoins, Landmark, Gavel } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { IMAGES, MARKETPLACE, PLATFORM_LOGOS } from "@/data/treasures";
import { servicesContent, serviceLabels } from "@/i18n/services";
import { scrollToId } from "@/lib/scroll";
import { Ornament } from "@/components/Ornament";

const SERVICE_ICONS = { raeumung: HomeIcon, entsorgung: Recycle, ankauf: HandCoins, nachlass: Landmark, beratung: Gavel };

const PlatformRow = ({ logo, icon, title, desc, href, to, onClick, testid }) => {
  const inner = (
    <div className="group/row flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-300 hover:bg-gold/10">
      <div className="w-11 h-11 rounded-lg bg-cream flex items-center justify-center overflow-hidden shrink-0 ring-1 ring-gold/20">
        {logo ? <img src={logo} alt={title} className="w-full h-full object-contain p-1" /> : icon}
      </div>
      <div className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-cream group-hover/row:text-gold transition-colors">{title}</span>
        <span className="block text-[11px] text-cream/55 truncate">{desc}</span>
      </div>
      <ChevronRight className="w-4 h-4 text-gold/60 group-hover/row:text-gold group-hover/row:translate-x-0.5 transition-all shrink-0" />
    </div>
  );
  if (to) return <Link to={to} onClick={onClick} data-testid={testid}>{inner}</Link>;
  return <a href={href} target="_blank" rel="noreferrer" data-testid={testid}>{inner}</a>;
};

export const Header = () => {
  const { t, lang, changeLang, langs, contact } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const services = servicesContent[lang];
  const labels = serviceLabels[lang];

  const goToService = (id) => {
    if (location.pathname === "/dienstleistungen") { scrollToId(id); }
    else { navigate("/dienstleistungen"); setTimeout(() => scrollToId(id), 400); }
    setOpenDrop(null);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setLangOpen(false); }, [location.pathname]);

  const links = [
    { to: "/cafe", label: t.nav.cafe },
    { to: "/brockenstube", label: t.nav.brocken },
    { to: "/dienstleistungen", label: t.nav.services },
    { to: "/neuigkeiten", label: t.nav.events },
    { to: "/geschichte", label: t.nav.story },
    { to: "/kontakt", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-[9000] transition-all duration-500 ${
        scrolled ? "bg-espresso-deep/90 backdrop-blur-xl shadow-[0_10px_40px_rgba(0,0,0,0.35)] py-3" : "bg-gradient-to-b from-espresso-deep/80 to-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src={IMAGES.logo} alt="Mara Wunderkammer" className="h-11 w-11 rounded-md object-cover ring-1 ring-gold/40 group-hover:ring-gold transition-all" />
          <div className="leading-none">
            <span className="block font-serif text-gold text-lg md:text-xl tracking-wide">Mara Wunderkammer</span>
            <span className="block text-[10px] md:text-[11px] tracking-[0.35em] text-cream/60 uppercase mt-0.5">Café &amp; Brock · Basel</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {links.map((l) => {
            const hasDrop = l.to === "/brockenstube" || l.to === "/dienstleistungen";
            const dropKey = l.to;
            const navClass = ({ isActive }) =>
              `relative inline-flex items-center gap-1 text-sm tracking-wide font-sans transition-colors duration-300 py-1 ${
                isActive ? "text-gold" : "text-cream/80 hover:text-gold"
              } after:content-[''] after:absolute after:left-0 after:-bottom-0.5 after:h-px after:bg-gold after:transition-all after:duration-300 ${
                isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
              }`;
            if (!hasDrop) {
              return (
                <NavLink key={l.to} to={l.to} end={l.end} data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`} className={navClass}>
                  {l.label}
                </NavLink>
              );
            }
            return (
              <div key={l.to} className="relative" onMouseEnter={() => setOpenDrop(dropKey)} onMouseLeave={() => setOpenDrop(null)}>
                <NavLink to={l.to} data-testid={`nav-${l.to.slice(1)}`} className={navClass}>
                  {l.label} <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${openDrop === dropKey ? "rotate-180" : ""}`} />
                </NavLink>
                {/* Dropdown panel */}
                <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 transition-all duration-300 ${openDrop === dropKey ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"}`}>
                  {dropKey === "/dienstleistungen" ? (
                    <div className="w-[300px] bg-[#1A100A] border border-gold/25 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.45)] p-2" data-testid="dropdown-dienstleistungen">
                      {services.map((s) => {
                        const Icon = SERVICE_ICONS[s.id];
                        return (
                          <button key={s.id} onClick={() => goToService(s.id)} data-testid={`drop-service-${s.id}`}
                            className="group/row w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-left transition-all duration-300 hover:bg-gold/10">
                            <span className="w-9 h-9 rounded-lg bg-espresso flex items-center justify-center shrink-0 group-hover/row:bg-gold transition-colors">
                              <Icon className="w-4 h-4 text-gold group-hover/row:text-espresso-deep transition-colors" />
                            </span>
                            <span className="flex-1 text-sm text-cream/85 group-hover/row:text-gold transition-colors">{s.title}</span>
                            <ChevronRight className="w-4 h-4 text-gold/50 group-hover/row:translate-x-0.5 transition-transform" />
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="w-[360px] bg-[#1A100A] border border-gold/25 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.45)] p-3" data-testid="dropdown-brockenstube">
                      <p className="px-4 pt-1 pb-3 text-[11px] tracking-wide text-gold/70">{t.platforms.same}</p>
                      <PlatformRow to="/brockenstube" onClick={() => setOpenDrop(null)} icon={<Store className="w-5 h-5 text-gold" />} title={t.platforms.website} desc={t.platforms.websiteDesc} testid="drop-platform-website" />
                      <PlatformRow href={MARKETPLACE.tutti} logo={PLATFORM_LOGOS.tutti} title={t.platforms.tutti} desc={t.platforms.extDesc} testid="drop-platform-tutti" />
                      <PlatformRow href={MARKETPLACE.ricardo} logo={PLATFORM_LOGOS.ricardo} title={t.platforms.ricardo} desc={t.platforms.extDesc} testid="drop-platform-ricardo" />
                      <PlatformRow href={MARKETPLACE.anibis} logo={PLATFORM_LOGOS.anibis} title={t.platforms.anibis} desc={t.platforms.extDesc} testid="drop-platform-anibis" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          {/* Call button */}
          <a
            href={`tel:${contact.phoneHref}`}
            data-testid="header-call-button"
            className="group relative hidden sm:inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-espresso-deep text-xs font-medium tracking-wide overflow-hidden shadow-[0_0_18px_rgba(200,169,126,0.5)] transition-all duration-300 hover:bg-gold-light hover:scale-105 hover:shadow-[0_0_28px_rgba(200,169,126,0.8)] active:scale-95"
          >
            <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out -skew-x-12 bg-gradient-to-r from-transparent via-white/55 to-transparent" />
            <Phone className="w-3.5 h-3.5 relative z-10" />
            <span className="relative z-10">{contact.phone}</span>
          </a>
          <a
            href={`tel:${contact.phoneHref}`}
            data-testid="header-call-button-mobile"
            aria-label="Anrufen"
            className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-full bg-gold text-espresso-deep shadow-[0_0_16px_rgba(200,169,126,0.5)] transition-transform hover:scale-105 active:scale-95"
          >
            <Phone className="w-4 h-4" />
          </a>

          {/* Language switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen((o) => !o)}
              data-testid="lang-switcher"
              className="flex items-center gap-2 text-cream/85 hover:text-gold transition-colors text-sm tracking-widest uppercase px-2 py-1 rounded"
            >
              <img src={`https://flagcdn.com/w40/${langs.find((l) => l.code === lang)?.flag}.png`} alt="" className="w-5 h-[15px] object-cover rounded-sm ring-1 ring-gold/30" />
              {langs.find((l) => l.code === lang)?.label}
            </button>
            {langOpen && (
              <div data-testid="lang-menu" className="absolute right-0 mt-2 w-48 bg-espresso-deep border border-gold/25 rounded-lg shadow-2xl overflow-hidden py-1">
                {langs.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { changeLang(l.code); setLangOpen(false); }}
                    data-testid={`lang-option-${l.code}`}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                      lang === l.code ? "text-gold bg-gold/5" : "text-cream/80 hover:text-gold hover:bg-gold/5"
                    }`}
                  >
                    <img src={`https://flagcdn.com/w40/${l.flag}.png`} alt="" className="w-6 h-[18px] object-cover rounded-sm ring-1 ring-gold/20 shrink-0" />
                    <span className="flex-1 text-left">{l.name}</span>
                    {lang === l.code && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className="lg:hidden text-cream hover:text-gold transition-colors"
            onClick={() => setMobileOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        data-testid="mobile-nav"
        className={`lg:hidden overflow-hidden transition-all duration-500 bg-[#1A100A] ${
          mobileOpen ? "max-h-[80vh] border-t border-gold/15" : "max-h-0"
        }`}
      >
        <nav className="flex flex-col px-8 py-6 gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `py-3 text-lg font-serif border-b border-gold/10 ${isActive ? "text-gold" : "text-cream/85"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Ornament className="mt-6 opacity-60" width={180} />
        </nav>
      </div>
    </header>
  );
};

export default Header;
